import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Shield, Truck, Video, Calendar, MessageCircle } from 'lucide-react';
import './home.scss';

const EMAIL_REGISTER_URL = '/medichat/register?return_to=%2Fmedichat%2Fchat';
const EMAIL_LOGIN_URL = '/medichat/login?return_to=%2Fmedichat%2Fchat';

const EmailAuthLinks = () => (
  <div className="mc-lp-email-entry">
    <a className="mc-lp-btn mc-lp-btn--email mc-lp-btn--lg" href={EMAIL_REGISTER_URL}>
      メールアドレスで始める
    </a>
    <a className="mc-lp-email-entry__login" href={EMAIL_LOGIN_URL}>
      ログインはこちら
    </a>
  </div>
);

const buildLineEntryUrl = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_DOMEIN ||
    (process.env.NODE_ENV === 'production' ? 'https://api.curenow.jp' : 'http://localhost:4000');
  return `${apiBaseUrl.replace(/\/$/, '')}/api/medichat/line_entry/start?path=${encodeURIComponent(path)}`;
};

const LINE_ICON = (
  <svg className="mc-lp-line-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.271.173-.508.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
);

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.unobserve(entry.target); } },
      { threshold: 0.12, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, isInView];
};

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mc-lp-faq__item ${open ? 'mc-lp-faq__item--open' : ''}`}>
      <button className="mc-lp-faq__question" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <ChevronDown className="mc-lp-faq__chevron" />
      </button>
      <div className="mc-lp-faq__answer"><p>{answer}</p></div>
    </div>
  );
};

const MainLpContainer = () => {
  const lineEntryUrl = buildLineEntryUrl();
  const [heroRef, heroVisible] = useInView();
  const [painRef, painVisible] = useInView();
  const [featRef, featVisible] = useInView();
  const [flowRef, flowVisible] = useInView();
  const [condRef, condVisible] = useInView();
  const [faqRef, faqVisible] = useInView();

  const PAIN_POINTS = [
    { emoji: '🏥', text: '病院が遠くて行けない' },
    { emoji: '⏰', text: '仕事でなかなか時間が取れない' },
    { emoji: '🌙', text: '夜間・休日に体調が悪くなった' },
    { emoji: '👶', text: '子連れでの通院が大変' },
    { emoji: '😷', text: '軽い症状でも待合室に行くのが億劫' },
    { emoji: '💊', text: '慢性疾患で毎回の受診が負担' },
  ];

  const FEATURES = [
    {
      icon: '🏠',
      title: '自宅からビデオ診察',
      desc: '移動・待合室ゼロ。スマホを開くだけで医師の診察を受けられます。',
    },
    {
      icon: '🏥',
      title: '保険診療対応',
      desc: '健康保険が使えるため、通常のクリニックと同じ自己負担額です。',
    },
    {
      icon: '⚡',
      title: '当日受診OK',
      desc: '空き枠があれば今日中に受診可能。最短3分で予約が完了します。',
    },
    {
      icon: '📦',
      title: 'お薬を自宅にお届け',
      desc: '自宅配送・バイク便（当日・東京23区）・全国1,800ヶ所以上の提携薬局でも受け取れます。',
    },
  ];

  const FLOW_STEPS = [
    { icon: <MessageCircle size={28} />, num: '1', title: 'チャットで問診', desc: '症状をチャット形式で入力。AIが最適な診療科をご案内します。' },
    { icon: <Calendar size={28} />, num: '2', title: '予約・医師選択', desc: '空き枠から日時を選んで予約。医師のプロフィールも確認できます。' },
    { icon: <Video size={28} />, num: '3', title: 'ビデオ診察', desc: '自宅やオフィスからスマホでビデオ診察。移動ゼロで受診できます。' },
    { icon: <Truck size={28} />, num: '4', title: 'お薬を受け取る', desc: 'ご自宅への配送・バイク便・提携薬局の3つから選べます。' },
  ];

  const SPECIALTIES = [
    { emoji: '🩺', name: '一般内科', diseases: ['風邪・発熱', '胃腸炎', '片頭痛', '便秘症'], insurance: true },
    { emoji: '❤️', name: '生活習慣病', diseases: ['糖尿病', '高血圧', '痛風', '脂質異常症'], insurance: true },
    { emoji: '🧠', name: '精神科・睡眠', diseases: ['不眠症', 'うつ病', '適応障害', 'パニック障害'], insurance: true },
    { emoji: '🌸', name: 'アレルギー', diseases: ['花粉症', 'アレルギー性鼻炎', '気管支喘息', '蕁麻疹'], insurance: true },
    { emoji: '✋', name: '皮膚科・多汗症', diseases: ['アトピー', 'ニキビ', '水虫', 'かぶれ', '多汗症'], insurance: true },
    { emoji: '💊', name: '自費診療', diseases: ['AGA', 'ダイエット', '低用量ピル', 'ED治療'], insurance: false },
  ];

  const FAQS = [
    { q: '初診でも利用できますか？', a: 'はい、初診の方もご利用いただけます。チャットで問診に回答後、そのまま診察予約が可能です。' },
    { q: '保険証は必要ですか？', a: '保険診療の場合は保険証のアップロードが必要です。自費診療の場合は不要です。' },
    { q: '診察料はいくらですか？', a: '保険診療のため、通常の医療機関と同じ自己負担額です。別途、薬の配送料がかかります。' },
    { q: '今日すぐ受診できますか？', a: '空き枠があれば当日中に診察を受けることができます。チャットから問診完了後、すぐに予約可能です。' },
    { q: '処方薬はどのように届きますか？', a: 'ご自宅への配送（通常1〜3日）、バイク便（当日・東京23区）、または全国1,800ヶ所以上の提携薬局でお受取りいただけます。' },
    { q: '対面診察が必要になった場合は？', a: '医師の判断で対面が必要と判断された場合は、クリニックをご案内します。' },
    { q: 'LINEを使わずに利用できますか？', a: 'はい。「メールアドレスで始める」から登録できます。ログイン後はブラウザから診察でき、診療の通知もメールで届きます。' },
  ];

  return (
    <div className="mc-lp">

      {/* ========================================
          ヘッダー
          ======================================== */}
      <header className="mc-lp-header">
        <img src="/logo_service.svg" alt="CureNow" className="mc-lp-header__logo" />
        <div className="mc-lp-header__actions">
          <a className="mc-lp-header__login" href={EMAIL_LOGIN_URL}>ログイン</a>
          <a href={lineEntryUrl} className="mc-lp-header__cta" target="_blank" rel="noreferrer">
            {LINE_ICON}
            <span>今すぐ受診</span>
          </a>
        </div>
      </header>

      {/* ========================================
          ヒーローセクション
          ======================================== */}
      <section className={`mc-lp-hero ${heroVisible ? 'mc-lp--visible' : ''}`} ref={heroRef}>
        <div className="mc-lp-hero__inner">
          {/* バッジ */}
          <div className="mc-lp-hero__badges">
            <span className="mc-lp-hero__badge mc-lp-hero__badge--green">✅ 保険診療対応</span>
            <span className="mc-lp-hero__badge">🆕 初診OK</span>
            <span className="mc-lp-hero__badge">⚡ 当日受診</span>
          </div>

          {/* 見出し */}
          <h1 className="mc-lp-hero__title">
            いつでも、どこでも。<br />
            <em>専門医に診てもらえる</em><br />
            オンライン診療
          </h1>
          <p className="mc-lp-hero__subtitle">
            問診・予約・ビデオ診察・薬の受け取りまで、<br />すべてスマホひとつで完結。
          </p>

          {/* プライマリCTA */}
          <a href={lineEntryUrl} className="mc-lp-btn mc-lp-btn--line mc-lp-btn--lg" target="_blank" rel="noreferrer">
            {LINE_ICON}
            LINEで診察をはじめる
          </a>
          <EmailAuthLinks />
          <p className="mc-lp-hero__cta-note">登録無料 · LINEでもメールでも始められます</p>

          {/* 実績バナー */}
          <div className="mc-lp-hero__stats">
            <div className="mc-lp-hero__stat">
              <span className="mc-lp-hero__stat-num">6,000<span>名以上</span></span>
              <span className="mc-lp-hero__stat-label">ご利用ユーザー</span>
            </div>
            <div className="mc-lp-hero__stat-sep" />
            <div className="mc-lp-hero__stat">
              <span className="mc-lp-hero__stat-num">6,500<span>件以上</span></span>
              <span className="mc-lp-hero__stat-label">診察実績</span>
            </div>
            <div className="mc-lp-hero__stat-sep" />
            <div className="mc-lp-hero__stat">
              <span className="mc-lp-hero__stat-num">1,800<span>ヶ所</span></span>
              <span className="mc-lp-hero__stat-label">提携薬局</span>
            </div>
          </div>
        </div>

        {/* 波形ディバイダー */}
        <div className="mc-lp-hero__wave" aria-hidden="true">
          <svg viewBox="0 0 375 40" preserveAspectRatio="none"><path d="M0,20 C100,40 275,0 375,20 L375,40 L0,40 Z" fill="#ffffff" /></svg>
        </div>
      </section>

      {/* ========================================
          お悩み共感セクション
          ======================================== */}
      <section className={`mc-lp-pain ${painVisible ? 'mc-lp--visible' : ''}`} ref={painRef}>
        <h2 className="mc-lp-section__title">こんなお悩みはありませんか？</h2>
        <div className="mc-lp-pain__grid">
          {PAIN_POINTS.map((p, i) => (
            <div className="mc-lp-pain__item" key={i}>
              <span className="mc-lp-pain__emoji">{p.emoji}</span>
              <span className="mc-lp-pain__text">{p.text}</span>
            </div>
          ))}
        </div>
        <div className="mc-lp-pain__resolve">
          <span>すべて</span>
          <strong>CureNow</strong>
          <span>で解決できます</span>
          <ChevronRight size={18} />
        </div>
      </section>

      {/* ========================================
          4つの特徴
          ======================================== */}
      <section className={`mc-lp-features ${featVisible ? 'mc-lp--visible' : ''}`} ref={featRef}>
        <h2 className="mc-lp-section__title">CureNowが選ばれる理由</h2>
        <div className="mc-lp-features__grid">
          {FEATURES.map((f, i) => (
            <div className="mc-lp-features__card" key={i}>
              <div className="mc-lp-features__icon">{f.icon}</div>
              <h3 className="mc-lp-features__title">{f.title}</h3>
              <p className="mc-lp-features__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ========================================
          ご利用の流れ
          ======================================== */}
      <section className={`mc-lp-flow ${flowVisible ? 'mc-lp--visible' : ''}`} ref={flowRef}>
        <div className="mc-lp-flow__inner">
          <h2 className="mc-lp-section__title mc-lp-section__title--light">ご利用の流れ</h2>
          <p className="mc-lp-section__subtitle mc-lp-section__subtitle--light">チャットに沿って進めるだけ。最短3分で予約完了。</p>

          <div className="mc-lp-flow__steps">
            {FLOW_STEPS.map((step, i) => (
              <React.Fragment key={i}>
                <div className="mc-lp-flow__step">
                  <div className="mc-lp-flow__step-head">
                    <div className="mc-lp-flow__step-num">{step.num}</div>
                    <div className="mc-lp-flow__step-icon">{step.icon}</div>
                  </div>
                  <h4 className="mc-lp-flow__step-title">{step.title}</h4>
                  <p className="mc-lp-flow__step-desc">{step.desc}</p>
                </div>
                {i < FLOW_STEPS.length - 1 && (
                  <div className="mc-lp-flow__connector" aria-hidden="true">
                    <ChevronRight size={18} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================
          対応診療科
          ======================================== */}
      <section className={`mc-lp-conditions ${condVisible ? 'mc-lp--visible' : ''}`} ref={condRef}>
        <h2 className="mc-lp-section__title">対応診療科</h2>
        <p className="mc-lp-section__subtitle">幅広い診療科に対応しています</p>

        <div className="mc-lp-conditions__grid">
          {SPECIALTIES.map((sp, i) => (
            <div className="mc-lp-conditions__card" key={i}>
              <div className="mc-lp-conditions__card-head">
                <span className="mc-lp-conditions__emoji">{sp.emoji}</span>
                <span className="mc-lp-conditions__name">{sp.name}</span>
                {sp.insurance && <span className="mc-lp-conditions__ins">保険</span>}
              </div>
              <div className="mc-lp-conditions__tags">
                {sp.diseases.map((d, j) => (
                  <span className="mc-lp-conditions__tag" key={j}>{d}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mc-lp-conditions__note">
          <Shield size={15} />
          <span>一般内科・生活習慣病・精神科・アレルギー・皮膚科・多汗症は<strong>保険適用</strong></span>
        </div>
      </section>

      {/* ========================================
          FAQ
          ======================================== */}
      <section className={`mc-lp-faq ${faqVisible ? 'mc-lp--visible' : ''}`} ref={faqRef}>
        <h2 className="mc-lp-section__title">よくあるご質問</h2>
        <div className="mc-lp-faq__list">
          {FAQS.map((faq, i) => (
            <FaqItem key={i} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </section>

      {/* ========================================
          底部CTA
          ======================================== */}
      <section className="mc-lp-bottom-cta">
        <h2 className="mc-lp-bottom-cta__title">まずは気軽に試してみませんか？</h2>
        <p className="mc-lp-bottom-cta__sub">
          LINEまたはメールアドレスで、今すぐ診察が始められます。<br />
          初めての方も安心してご利用ください。
        </p>
        <a href={lineEntryUrl} className="mc-lp-btn mc-lp-btn--line mc-lp-btn--lg" target="_blank" rel="noreferrer">
          {LINE_ICON}
          LINEで友だち追加して始める
        </a>
        <EmailAuthLinks />
        <p className="mc-lp-bottom-cta__note">登録無料 · 保険証不要で今すぐ体験</p>
      </section>

      {/* ========================================
          クリニック情報・フッター
          ======================================== */}
      <footer className="mc-lp-footer">
        <div className="mc-lp-footer__clinic">
          <p className="mc-lp-footer__clinic-name">恵比寿駅前メンタルクリニック</p>
          <p className="mc-lp-footer__clinic-addr">〒150-0022 東京都渋谷区恵比寿南1-3-7 隅越ビル3階</p>
          <p className="mc-lp-footer__clinic-tel">TEL: 03-6899-5958</p>
        </div>
        <nav className="mc-lp-footer__legal">
          <a href="https://www.notion.so/CureNow-2cba4be0fa7080cd8e3ee5b623afb4a7" target="_blank" rel="noreferrer">利用規約</a>
          <span aria-hidden="true">|</span>
          <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080bc9233f81808fbb4d0" target="_blank" rel="noreferrer">プライバシーポリシー</a>
          <span aria-hidden="true">|</span>
          <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080edb59ec9ef331e9ee9" target="_blank" rel="noreferrer">キャンセルポリシー</a>
          <span aria-hidden="true">|</span>
          <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa708045a472fab1f4069ba1" target="_blank" rel="noreferrer">特定商取引法に基づく表示</a>
          <span aria-hidden="true">|</span>
          <a href={EMAIL_LOGIN_URL}>メールでログイン</a>
        </nav>
      </footer>

      {/* ========================================
          固定底部CTAバー（スクロール中も常に表示）
          ======================================== */}
      <div className="mc-lp-sticky-bar">
        <a href={lineEntryUrl} className="mc-lp-btn mc-lp-btn--line mc-lp-btn--sticky" target="_blank" rel="noreferrer">
          {LINE_ICON}
          今すぐ無料で診察をはじめる
        </a>
        <a className="mc-lp-sticky-bar__email" href={EMAIL_REGISTER_URL}>
          メールアドレスで始める
        </a>
      </div>

    </div>
  );
};

export default MainLpContainer;
